/**
 * WP-12 settings screen. Reachable from the title menu. Surfaces the
 * preferences that already persisted piecemeal (volume/mute, skip-cinematic)
 * plus the new ones (CRT-shader overlay, default AI difficulty, F/S counter),
 * all stored in ui/save.ts. Toggles apply live so the player sees the effect.
 */
import { DIFFICULTY_NAMES } from '../rules/ai';
import { audio } from '../audio/engine';
import { applyCrt } from './crt';
import { showFps } from './fps';
import {
  getCrt, setCrt, getDefaultDifficulty, setDefaultDifficulty,
  getFps, setFps, getSkipCinematic, setSkipCinematic,
} from './save';
import { fitScreen } from './screen';
import { ICON_ANCHOR, ICON_GEAR, ICON_VOLUME, ICON_VOLUME_OFF } from './icons';

const CSS = `
.ar-settings { font-family: Georgia, 'Times New Roman', serif; color: hsl(28, 55.6%, 10.6%);
  --navy:hsl(217, 53.7%, 16.1%); --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%); --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%);
  --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%); --head:hsl(23, 66.7%, 21.2%);
  opacity: 1; transition: opacity 0.22s ease; }
.ar-settings.leaving { opacity: 0; }
.ar-settings .hd { display:flex; flex-wrap:wrap; align-items:stretch; background: linear-gradient(180deg,hsl(219, 52.2%, 22.2%),var(--navy)); color:hsl(43, 100%, 84.5%); border:2px solid hsl(219, 58.3%, 9.4%);
  border-bottom:3px solid var(--brass-d); }
/* back button | beveled divider | left-aligned heading — mirrors the game/buy
   bar headers (a 1px dark rule with a light inset highlight = a bevelled groove). */
.ar-settings .hd h2 { margin:0 0 0 14px; flex:1; display:flex; align-items:center; font-size:22px; letter-spacing:1px; text-align:left;
  padding:11px 18px; border-left:1px solid hsla(0, 0%, 0%, 0.35); box-shadow: inset 1px 0 0 hsla(0, 0%, 100%, 0.12); }
/* close/back, return-to-menu, etc. share ONE chrome (identical S/L) and differ only
   in --h (hue): red for close/back, orange for the return button. */
.ar-settings .ar-cbtn { --h:4; font-family:inherit; font-weight:700; cursor:pointer; color:#fff;
  background:linear-gradient(180deg, hsl(var(--h),62%,55%), hsl(var(--h),62%,47%) 55%, hsl(var(--h),62%,39%));
  border:2px solid hsl(var(--h),48%,30%); border-radius:4px;
  box-shadow: inset 0 1px 0 hsla(0,0%,100%,0.4), inset 0 -2px 2px hsla(0,0%,0%,0.22), 0 1px 2px hsla(0,0%,0%,0.3);
  text-shadow: 0 1px 1px hsla(0,0%,0%,0.45); transition: filter 0.08s; }
.ar-settings .ar-cbtn:hover { filter:brightness(1.08); }
.ar-settings .ar-cbtn:active { box-shadow: inset 0 2px 4px hsla(0,0%,0%,0.4); transform: translateY(1px); }
.ar-settings .hd .back { align-self:center; margin-left:14px; font-size:15px; padding:9px 18px; }
.ar-settings .hd .back .bico { width:15px; height:15px; display:inline-block; vertical-align:-2px; margin-right:8px; }
.ar-settings .body { background: linear-gradient(180deg,hsl(44, 55.6%, 89.4%),var(--parch) 30%,var(--parch2));
  border:2px solid var(--wood); border-top:none; padding:6px 18px 16px; }
.ar-settings .row { display:flex; align-items:center; justify-content:space-between; gap:14px;
  padding:12px 2px; border-bottom:1px solid hsla(31, 47.6%, 28.4%, 0.22); }
.ar-settings .row:last-of-type { border-bottom:none; }
.ar-settings .row .lab { min-width:0; font-weight:700; color:var(--head); font-size:15px; }
.ar-settings .row .lab small { display:block; font-weight:400; font-style:italic; color:hsl(31, 47.6%, 28.4%); font-size:11.5px; }
/* min-width:0 lets the control shrink to fit (the slider gives up width first)
   rather than overflowing — so rows stay side-by-side far longer before stacking. */
.ar-settings .ctl { display:flex; align-items:center; gap:9px; min-width:0; }
.ar-settings input[type=range] { width:170px; min-width:0; max-width:100%; accent-color:hsl(105, 38.4%, 43.9%); }
/* mute button at the left of the volume slider — silver, like the in-game zoom
   buttons (opaque here so it reads as silver over the parchment, not tinted). */
.ar-settings .vmute { display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px;
  flex:0 0 auto; cursor:pointer; font-size:15px; line-height:1; padding:0; border-radius:6px; color:#fff;
  background:linear-gradient(180deg, hsl(210, 11%, 56%), hsl(210, 10%, 42%) 60%, hsl(210, 10%, 34%));
  border:2px solid hsl(210, 14%, 12%); box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.25); }
.ar-settings .vmute:hover { filter:brightness(1.1); }
.ar-settings .vmute:active { transform:translateY(1px); }
.ar-settings .vmute svg { width:19px; height:19px; display:block; }
.ar-settings .hd h2 .ic { display:inline-flex; vertical-align:-3px; margin-right:8px; }
.ar-settings .hd h2 .ic svg { width:20px; height:20px; display:block; }
.ar-settings .ret .ic { display:inline-flex; vertical-align:-3px; margin-right:7px; }
.ar-settings .ret .ic svg { width:18px; height:18px; display:block; }
.ar-settings select { font-family:inherit; font-size:13px; padding:5px 8px; color:hsl(28, 55.6%, 10.6%);
  border:1px solid var(--wood-d); border-radius:4px; background:hsl(200, 100%, 89%);
  box-shadow:inset 0 1px 0 hsla(0, 0%, 100%, 0.5); cursor:pointer; }
.ar-settings .vval { width:38px; text-align:right; font-variant-numeric:tabular-nums; }
/* pill toggle — recessed track with a sliding brass knob and a ✓/✕ state mark
   (✕ at rest on the right, ✓ revealed on the left when switched on). */
.ar-settings .toggle { position:relative; width:52px; height:22px; border:2px solid var(--wood-d); border-radius:13px;
  background:hsl(44, 32.1%, 68.8%); cursor:pointer; transition:background .12s;
  box-shadow: inset 0 1px 3px hsla(30, 54.2%, 18.8%, 0.4); }
.ar-settings .toggle::before { content:'✕'; position:absolute; top:50%; right:8px; transform:translateY(-50%);
  font-size:9px; font-weight:700; line-height:1; color:hsla(30, 54.2%, 18.8%, 0.55); transition:color .12s; }
.ar-settings .toggle.on::before { content:'✓'; right:auto; left:8px; color:hsl(0, 0%, 100%); }
.ar-settings .toggle::after { content:''; position:absolute; top:2px; left:2px; width:18px; height:18px; border-radius:50%;
  background:linear-gradient(180deg,hsl(0, 0%, 100%),hsl(42, 42.6%, 73.3%)); box-shadow:0 1px 2px hsla(0, 0%, 0%, 0.45); transition:left .14s ease; }
.ar-settings .toggle.on { background:hsl(105, 38.4%, 43.9%); }
.ar-settings .toggle.on::after { left:32px; }
/* overlay: a dim full-viewport backdrop holding the centered panel; click-outside
   closes. Fixed so it covers the viewport whether opened over the game or the title. */
.ar-settings-backdrop { position:fixed; inset:0; z-index:50;
  background: radial-gradient(ellipse at center, hsla(219, 51.8%, 16.3%, 0.72), hsla(220, 60%, 3.9%, 0.86));
  opacity:1; transition: opacity 0.22s ease; }
.ar-settings-backdrop.leaving { opacity:0; }
/* only once genuinely cramped: stack each row's control under its label and let the
   slider / select fill the width, so nothing spills off-screen; shrink the header. */
@media (max-width: 380px) {
  .ar-settings .row { flex-direction:column; align-items:stretch; gap:9px; }
  .ar-settings .ctl { width:100%; }
  .ar-settings input[type=range] { flex:1 1 auto; width:auto; min-width:0; }
  .ar-settings select { width:100%; }
  .ar-settings .vval { margin-left:auto; }
  .ar-settings .hd h2 { font-size:18px; padding:9px 12px; margin-left:10px; }
  .ar-settings .hd h2 .ic svg { width:17px; height:17px; }
  .ar-settings .hd .back { margin-left:10px; padding:8px 12px; font-size:14px; }
  .ar-settings .body { padding:6px 13px 14px; }
}
/* footer action buttons below the settings rows. They sit side by side on one
   row when there's width for it, and wrap to a full-width stack when there isn't.
   The last settings row's border separates them from the rows (one divider only). */
.ar-settings .foot { padding:24px 2px 4px; display:flex; flex-wrap:wrap; justify-content:center; gap:10px; }
/* the destructive "Reset Progress" (red) and "Return to Main Menu" (orange) share
   the same chrome; they differ only in --h (hue). */
.ar-settings .reset { --h:4; }
.ar-settings .ret { --h:28; }
.ar-settings .ret, .ar-settings .reset { flex:1 1 210px; font-size:15px; padding:11px 18px; text-align:center; }
/* armed reset shows a warning ring so the confirm tap is unmistakable */
.ar-settings .reset.armed { box-shadow: inset 0 1px 0 hsla(0,0%,100%,0.4), 0 0 0 2px hsla(4,72%,58%,0.55); }
.ar-settings .reset .rico { width:15px; height:15px; display:inline-block; vertical-align:-2px; margin-right:8px; }
`;

// Header-button glyphs as SVG shapes (inherit the button's text colour), instead of
// the ◂ / ✕ text characters — matches the buy/zoom stepper glyph treatment.
const BACK_ICON = '<svg class="bico" viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2 L3.5 6 L7.5 10 Z" fill="currentColor"/></svg>';
const CLOSE_ICON = '<svg class="bico" viewBox="0 0 12 12" aria-hidden="true"><path d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
// Reset Progress — a counter-clockwise refresh arrow (Lucide "rotate-ccw").
const RESET_ICON = '<svg class="rico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>';

export interface SettingsOpts {
  container: HTMLElement;
  onBack: () => void;
  /** Render as a dimmed overlay (in-game) rather than a full-page menu screen.
   *  Clicking the backdrop closes it, same as the header button. */
  overlay?: boolean;
  /** Header button label — defaults to "◂ Back"; in-game passes "✕ Close". */
  backLabel?: string;
  /** When set, a "Return to main menu" button is appended below the settings. */
  onReturnToMenu?: () => void;
  /** When set, a destructive reset button is appended below the settings (two-tap
   *  confirm). Used by the campaign screen (reset progress) and the main-menu
   *  settings (clear all data). */
  onResetProgress?: () => void;
  /** Label for the reset button — defaults to "Reset Progress". */
  resetLabel?: string;
}

export interface SettingsHandle {
  destroy(): void;
}

export function showSettings(opts: SettingsOpts): SettingsHandle {
  if (!document.getElementById('ar-settings-style')) {
    const style = document.createElement('style');
    style.id = 'ar-settings-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const diffOpts = DIFFICULTY_NAMES.map(
    (n, i) => `<option value="${i + 1}"${i + 1 === getDefaultDifficulty() ? ' selected' : ''}>Lv ${i + 1} — ${n}</option>`,
  ).join('');
  const vol = Math.round(audio.getVolume() * 100);

  const wrap = document.createElement('div');
  wrap.className = 'ar-settings';
  wrap.id = 'ar-settings';
  wrap.innerHTML = `
    <div class="hd"><button class="ar-cbtn back" id="ar-set-back">${opts.overlay ? CLOSE_ICON : BACK_ICON}${opts.backLabel ?? (opts.overlay ? 'Close' : 'Back')}</button><h2><span class="ic">${ICON_GEAR}</span>Settings</h2></div>
    <div class="body">
      <div class="row">
        <div class="lab">Master volume<small>Music &amp; sound effects</small></div>
        <div class="ctl">
          <button class="vmute" id="ar-set-mute" type="button" aria-label="Mute">${ICON_VOLUME}</button>
          <input type="range" id="ar-set-vol" min="0" max="100" value="${vol}">
          <span class="vval" id="ar-set-volval">${vol}%</span>
        </div>
      </div>
      <div class="row">
        <div class="lab">Default difficulty<small>Which admiral the buy screen opens on</small></div>
        <div class="ctl"><select id="ar-set-diff">${diffOpts}</select></div>
      </div>
      <div class="row">
        <div class="lab">Skip combat cinematic<small>Resolve battles instantly</small></div>
        <div class="ctl"><div class="toggle${getSkipCinematic() ? ' on' : ''}" id="ar-set-skip" role="switch"></div></div>
      </div>
      <div class="row">
        <div class="lab">CRT shader<small>Retro scanlines &amp; vignette</small></div>
        <div class="ctl"><div class="toggle${getCrt() ? ' on' : ''}" id="ar-set-crt" role="switch"></div></div>
      </div>
      <div class="row">
        <div class="lab">Show FPS<small>Show the frames per second count on the bottom right of the screen</small></div>
        <div class="ctl"><div class="toggle${getFps() ? ' on' : ''}" id="ar-set-fps" role="switch"></div></div>
      </div>
      ${(opts.onReturnToMenu || opts.onResetProgress) ? `<div class="foot">
        ${opts.onResetProgress ? `<button class="ar-cbtn reset" id="ar-set-reset">${RESET_ICON}${opts.resetLabel ?? 'Reset Progress'}</button>` : ''}
        ${opts.onReturnToMenu ? `<button class="ar-cbtn ret" id="ar-set-menu"><span class="ic">${ICON_ANCHOR}</span>Return to Main Menu</button>` : ''}
      </div>` : ''}
    </div>`;
  // In-game: nest the panel in a dim backdrop that closes on click-outside. As a
  // menu screen: mount the panel directly. fitScreen centers/scales it either way.
  const backdrop = opts.overlay ? document.createElement('div') : null;
  if (backdrop) { backdrop.className = 'ar-settings-backdrop'; backdrop.appendChild(wrap); opts.container.appendChild(backdrop); }
  else opts.container.appendChild(wrap);
  const stopFit = fitScreen(wrap, { center: true });
  wrap.style.maxWidth = '760px'; // override the shared 2000px cap — settings is a narrow form

  const $ = (id: string): HTMLElement => wrap.querySelector(`#${id}`)!;

  // Master volume + integrated mute: the mute button drops the level to 0 and restores
  // the last level on the next click, so the slider is the single audio control. Any
  // pre-existing mute flag (e.g. the in-game mute) is folded in on entry — represented
  // as volume 0 — so the two never disagree.
  const volEl = $('ar-set-vol') as HTMLInputElement;
  const muteBtn = $('ar-set-mute');
  let lastVol = vol > 0 ? vol : 80; // level to restore when un-muting
  const renderVol = (v: number): void => {
    volEl.value = String(v);
    $('ar-set-volval').textContent = `${v}%`;
    muteBtn.innerHTML = v === 0 ? ICON_VOLUME_OFF : ICON_VOLUME;
    muteBtn.setAttribute('aria-label', v === 0 ? 'Unmute' : 'Mute');
  };
  const setVol = (v: number): void => {
    audio.setMuted(false); // volume is the single control here — clear any stale mute flag
    audio.setVolume(v / 100);
    renderVol(v);
  };
  setVol(audio.isMuted() ? 0 : vol); // normalise on mount
  volEl.addEventListener('input', () => {
    const v = Number(volEl.value);
    if (v > 0) lastVol = v;
    setVol(v);
  });
  muteBtn.addEventListener('click', () => {
    const cur = Number(volEl.value);
    if (cur > 0) { lastVol = cur; setVol(0); }
    else { setVol(lastVol > 0 ? lastVol : 80); }
  });

  // a reusable pill-toggle binding (reads/writes via the supplied setter)
  const bindToggle = (id: string, set: (on: boolean) => void): void => {
    const t = $(id);
    t.addEventListener('click', () => {
      const on = !t.classList.contains('on');
      t.classList.toggle('on', on);
      set(on);
    });
  };
  bindToggle('ar-set-skip', (on) => setSkipCinematic(on));
  bindToggle('ar-set-crt', (on) => {
    setCrt(on);
    applyCrt(on); // live preview
  });
  bindToggle('ar-set-fps', (on) => {
    setFps(on);
    showFps(on); // live preview
  });

  ($('ar-set-diff') as HTMLSelectElement).addEventListener('change', (ev) => {
    setDefaultDifficulty(Number((ev.target as HTMLSelectElement).value));
  });

  const leave = (then: () => void): void => {
    wrap.classList.add('leaving');
    backdrop?.classList.add('leaving');
    window.setTimeout(then, 230);
  };
  $('ar-set-back').addEventListener('click', () => leave(opts.onBack));
  // Click-outside closes, like the button. The panel wrap is pinned to full height
  // and flex-centres its content, so the empty margin above/below the visible form
  // is the wrap's own area — treat a click there (ev.target === wrap) as "outside"
  // too, not just the backdrop, so a short panel still closes on an outside click.
  backdrop?.addEventListener('click', (ev) => {
    if (ev.target === backdrop || ev.target === wrap) leave(opts.onBack);
  });
  wrap.querySelector('#ar-set-menu')?.addEventListener('click', () => leave(opts.onReturnToMenu!));

  // Reset Progress is destructive, so it takes two taps: the first arms the button
  // (re-labels + shows a warning ring), the second within 3s actually resets. The
  // armed state disarms itself on a timeout so a stray first tap is harmless.
  const resetBtn = wrap.querySelector('#ar-set-reset');
  if (resetBtn) {
    let armed = false;
    let armTimer = 0;
    const disarm = (): void => {
      armed = false;
      window.clearTimeout(armTimer);
      resetBtn.classList.remove('armed');
      resetBtn.innerHTML = `${RESET_ICON}${opts.resetLabel ?? 'Reset Progress'}`;
    };
    resetBtn.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        resetBtn.classList.add('armed');
        resetBtn.innerHTML = `${RESET_ICON}Tap again to confirm`;
        armTimer = window.setTimeout(disarm, 3000);
        return;
      }
      window.clearTimeout(armTimer);
      leave(opts.onResetProgress!);
    });
  }

  return { destroy: () => { stopFit(); (backdrop ?? wrap).remove(); } };
}
